import { MapPin, Phone, Mail, Clock, MessageSquare, Train, Navigation } from "lucide-react";
import { BUSINESS } from "@/config";
import SEO from "@/components/SEO";
import { Reveal, MaskedLines } from "@/components/Reveal";
import InquiryForm from "@/components/InquiryForm";
import { BookButton } from "@/components/CTAButtons";
import { track, directionsUrl } from "@/lib/analytics";

export default function Contact() {
  const mapsQuery = encodeURIComponent(`${BUSINESS.addressLine1}, ${BUSINESS.addressLine2}`);
  return (
    <>
      <SEO
        title="Contact & Location — Hell's Kitchen Salon, NYC"
        description="Visit Maison Neuf at 555 Ninth Avenue, Hell's Kitchen, Manhattan. Call or text to book, get subway directions, hours, and our arrival & cancellation policies."
      />
      <section className="mx-auto max-w-[1400px] px-5 pt-36 pb-12 md:px-10 md:pt-44" data-testid="contact-hero">
        <p className="label mb-6 text-wine">Contact & Location</p>
        <h1 className="max-w-4xl font-display text-5xl leading-[1.0] md:text-8xl">
          <MaskedLines lines={["Let's find", "your time."]} />
        </h1>
      </section>

      <section className="mx-auto grid max-w-[1400px] gap-14 px-5 pb-24 md:grid-cols-12 md:px-10" data-testid="contact-body">
        {/* LEFT: details */}
        <div className="md:col-span-5">
          <Reveal>
            <div className="space-y-8">
              <div className="flex gap-4">
                <MapPin className="mt-1 h-5 w-5 shrink-0 text-wine" strokeWidth={1.5} />
                <div>
                  <p className="label mb-2">Address</p>
                  <p className="text-foreground/90">{BUSINESS.addressLine1}<br />{BUSINESS.addressLine2}</p>
                  <p className="mt-1 text-sm text-muted-foreground">{BUSINESS.crossStreets}</p>
                </div>
              </div>
              <div className="flex gap-4">
                <Phone className="mt-1 h-5 w-5 shrink-0 text-wine" strokeWidth={1.5} />
                <div>
                  <p className="label mb-2">Call or text</p>
                  <a href={BUSINESS.phoneHref} data-testid="contact-call" onClick={() => track("phone_click", { location: "contact" })} className="block text-foreground/90 hover:text-foreground">{BUSINESS.phoneDisplay}</a>
                  <a href={BUSINESS.smsHref} data-testid="contact-text" onClick={() => track("phone_click", { location: "contact", type: "sms" })} className="mt-1 inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground">
                    <MessageSquare className="h-4 w-4" strokeWidth={1.5} /> Text us
                  </a>
                </div>
              </div>
              <div className="flex gap-4">
                <Mail className="mt-1 h-5 w-5 shrink-0 text-wine" strokeWidth={1.5} />
                <div>
                  <p className="label mb-2">Email</p>
                  <a href={BUSINESS.emailHref} data-testid="contact-email" onClick={() => track("email_click", { location: "contact" })} className="text-foreground/90 hover:text-foreground">{BUSINESS.email}</a>
                </div>
              </div>
              <div className="flex gap-4">
                <Train className="mt-1 h-5 w-5 shrink-0 text-wine" strokeWidth={1.5} />
                <div>
                  <p className="label mb-2">By subway</p>
                  <ul className="space-y-1 text-sm text-muted-foreground">
                    {BUSINESS.transit.map((t, i) => <li key={i}>{t}</li>)}
                  </ul>
                </div>
              </div>
              <div className="flex gap-4">
                <Clock className="mt-1 h-5 w-5 shrink-0 text-wine" strokeWidth={1.5} />
                <div className="w-full">
                  <p className="label mb-3">Hours</p>
                  <ul className="space-y-2 text-sm">
                    {BUSINESS.hours.map((h) => (
                      <li key={h.day} className="flex justify-between border-b border-border pb-2 text-foreground/90">
                        <span>{h.day}</span><span className="text-muted-foreground">{h.time}</span>
                      </li>
                    ))}
                  </ul>
                  <p className="mt-3 text-xs text-muted-foreground">Holiday hours may vary — please call ahead.</p>
                </div>
              </div>
              <div className="pt-2">
                <BookButton testid="contact-book-button" />
              </div>
            </div>
          </Reveal>
        </div>

        {/* RIGHT: form */}
        <div className="md:col-span-7 md:border-l md:border-border md:pl-12">
          <Reveal>
            <p className="label mb-3 text-wine">Quick appointment inquiry</p>
            <p className="mb-8 max-w-md text-muted-foreground">Prefer online booking? Use the Book Online button. For questions or special requests, send us a note.</p>
            <InquiryForm />
          </Reveal>
        </div>
      </section>

      {/* MAP */}
      <section className="border-t border-border" data-testid="contact-map">
        <div className="mx-auto flex max-w-[1400px] items-center justify-between gap-4 px-5 py-5 md:px-10">
          <p className="label">Find us in Hell's Kitchen</p>
          <a
            href={directionsUrl(BUSINESS)}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="contact-directions"
            onClick={() => track("directions_click", { location: "contact_map" })}
            className="inline-flex items-center gap-2 border border-foreground/40 px-5 py-3 font-mono text-xs uppercase tracking-[0.16em] text-foreground transition-colors hover:border-foreground"
          >
            <Navigation className="h-4 w-4" strokeWidth={1.5} /> Get Directions
          </a>
        </div>
        <div className="aspect-[16/9] w-full md:aspect-[21/7]">
          <iframe
            title="Maison Neuf location map"
            src={`https://www.google.com/maps?q=${mapsQuery}&output=embed`}
            className="h-full w-full grayscale contrast-125"
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          />
        </div>
      </section>

      {/* POLICIES */}
      <section className="mx-auto max-w-[1400px] px-5 py-20 md:px-10 md:py-24" data-testid="contact-policies">
        <div className="grid gap-10 md:grid-cols-2">
          <Reveal>
            <div className="border border-border p-8">
              <p className="label mb-4 text-wine">Arrival</p>
              <p className="text-muted-foreground">Please arrive 5–10 minutes early to settle in. Arriving more than 15 minutes late may shorten or forfeit your service so we can honor the next guest's time.</p>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="border border-border p-8">
              <p className="label mb-4 text-wine">Cancellations</p>
              <p className="text-muted-foreground">We kindly ask for 24 hours' notice. Late cancellations and no-shows may incur a fee of up to 50% of the booked service. Full details in our <a href="/faq" className="text-foreground link-underline">policies & FAQ</a>.</p>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
